import {Injectable} from '@angular/core';
import {AbstractControl, UntypedFormGroup} from '@angular/forms';
import {ActivatedRoute, Router} from '@angular/router';
import {CustomScrollingService} from '@app/services/custom-scrolling.service';
import {ProductDetailsPageHistoryService} from '@app/services/user-registration/product-details-page-history.service';
import {take, takeUntil} from 'rxjs/operators';
import {UserRegistrationRedirectService} from '@app/services/user-registration/user-registration-redirect.service';
import {UserRegistrationConfigService} from '@app/services/user-registration/user-registration-config.service';
import {Subject} from 'rxjs';

@Injectable({
  providedIn: 'root'
})

export class B2bRegistrationService {

  constructor(
    private router: Router,
    private scrollingService: CustomScrollingService,
    private productDetailsPageHistory: ProductDetailsPageHistoryService,
    private activatedRoute: ActivatedRoute,
    private userRegistrationRedirectService: UserRegistrationRedirectService,
    private userRegistrationConfigService: UserRegistrationConfigService,
  ) {
  }

  public errorMessages = {
    company: 'registration.error.noError',
    street: 'registration.error.noError',
    addressLine2: 'registration.error.noError',
    city: 'registration.error.noError',
    postalCode: 'registration.error.noError',
    countryCode: 'registration.error.noError',
    regionCode: 'registration.error.noError',
    lineOfBusiness: 'registration.error.noError'
  };

  public untilDestroyed$: Subject<void> = new Subject();

  public form: UntypedFormGroup;

  public showErrorMessages = false;

  setCompanyNameFromUrlParameter() {
    this.activatedRoute.queryParamMap.pipe(take(1), takeUntil(this.untilDestroyed$)).subscribe((params) => {
      const company = params.get('companyName');
      if (company) {
        this.form.controls.company.setValue(company);
      }
    });
  }

  resetNavigation(): void {
    this.router.navigateByUrl((this.userRegistrationRedirectService.getCurrent() || '/')).then(() => {
      this.userRegistrationRedirectService.resetNavigationUrl();
      this.userRegistrationConfigService.resetConfig();
    });
  }

  public getMaxLength(field: string): number {
    switch (field) {
      case 'postalCode':
        return 10;
      case 'street':
        return 60;
      default:
        return 40;
    }
  }

  public validateAllInputs(): void {
    if (this.showErrorMessages) {
      Object.keys(this.errorMessages).forEach(field => this.setErrorMessage(field));
    }
  }

  public validateInput(field: string): void {
    this.setErrorMessage(field)
  }

  onCancel(): void {
    this.scrollingService.scrollTop();
    if (this.activatedRoute.snapshot.queryParamMap.get('fastTrack') === 'true') {
      this.router.navigateByUrl(this.productDetailsPageHistory.getCurrent() || '/');
    } else {
      this.router.navigateByUrl('/selectionType');
    }
  }

  hasMatchError(field1: string, field2: string): boolean {
    const matchErrors = [`checkMatch-${field1}:${field2}`, `checkMatch-${field2}:${field1}`];
    return !this.form.get(field1)?.errors
      && Object.keys(this.form.errors || {}).some(err => matchErrors.includes(err));
  }

  hasFieldError(field: string, errorType?: string): boolean {
    const hasError = this.showErrorMessages && this.form.get(field)?.errors;
    return errorType ? hasError && this.form.get(field)?.errors[errorType] : hasError;
  }

  getErrorStatus(field: string): string {
    return this.showErrorMessages
    && (Object.values(this.form.errors || {}).some(({field1, field2}) => field1 === field || field2 === field)
      || this.hasFieldError(field))
      ? 'Error' : 'Standard';
  }

  setErrorMessages(messages: any): void {
    this.errorMessages = messages;
  }

  setErrorMessage(field: string): void {
    const formField = this.getFormControl(field);
    const errors = formField?.errors || {};
    let error = 'registration.error.noError';
    const matchValidatorError = this.getMatchValidationError(field);
    if (matchValidatorError || this.hasFieldError(field)) {
      const errorName = this.fieldNameParser(Object.keys(errors)[0]);
      error = `registration.error.${matchValidatorError || errorName}`;
    }
    this.errorMessages[field] = error;
  }

  getErrorMessage(field: string): string {
    return this.errorMessages[field];
  }

  setShowErrorMessages(state: boolean): void {
    this.showErrorMessages = state;
  }

  isMaxLengthError(field: string): boolean {
    return this.getErrorMessage(field) === 'registration.error.maxLength';
  }

  getMatchValidationError(field: string): string {
    if (field === 'company' && (this.hasMatchError(field, 'street') || this.hasMatchError(field, 'city'))) {
      return 'duplicatesAddressOrCity'
    }

    if (field === 'city' && (this.hasMatchError(field, 'street') || this.hasMatchError(field, 'addressLine2'))) {
      return 'duplicatesAddressOrAddressLine2'
    }

    if (field === 'addressLine2' && this.hasMatchError(field, 'company')) {
      return 'duplicatesCompany'
    }

    if (field === 'addressLine2' && this.hasMatchError(field, 'street')) {
      return 'duplicatesAddress'
    }

    if (this.hasMatchError(field, 'addressLine2')) {
      return 'duplicatesAddressLine2'
    }

    if (this.hasMatchError(field, 'city')) {
      return 'duplicatesCity'
    }

    if (this.hasMatchError(field, 'company')) {
      return 'duplicatesCompany'
    }

    return ''
  }

  resetErrors(): void {
    this.errorMessages = {
      company: 'registration.error.noError',
      street: 'registration.error.noError',
      addressLine2: 'registration.error.noError',
      city: 'registration.error.noError',
      postalCode: 'registration.error.noError',
      countryCode: 'registration.error.noError',
      regionCode: 'registration.error.noError',
      lineOfBusiness: 'registration.error.noError'
    };
    this.showErrorMessages = false;
  }

  getFormControl(field: string): AbstractControl {
    return this.form.get(field);
  }

  resetInputs(controlNames: string[]): void {
    controlNames.forEach(name => this.getFormControl(name)?.patchValue(''));
  }

  private fieldNameParser(field: string): string {
    switch (field) {
      case 'maxlength':
        return 'maxLength';
      case 'onlyLatin1Characters':
        return 'nonLatin';
      case 'atLeastOneLetter':
        return 'oneLetter';
      case 'regExp':
        return 'regExpPostalCode';
      default:
        return field;
    }
  }
}
